import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { add, addDays, endOfDay, formatISO, isAfter, isBefore, parseISO, startOfDay, sub } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { defaultProjectWidgetsConfig } from 'src/app/classes/widget/project-widgets-config';
import { DashboardPanelComponent } from 'src/app/components/project-pages/dashboard/dashboard-panel/dashboard-panel.component';
import { WidgetV2 } from 'src/app/interfaces/api/chart';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { Dashboard } from 'src/app/interfaces/shared/dashboard';
import { ReplayParams } from 'src/app/interfaces/shared/replayParams';
import { ChartService } from 'src/app/services/api/charts/chart.service';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, DatePickerComponent, RangePickerComponent, SpinnerComponent } from 'ui-library';
import { WidgetEditorComponent } from '../dialogs/widget-editor/widget-editor.component';
import { openWidgetCatalog } from 'src/app/components/project-pages/dashboard/dialogs/widget-catalog/widget-catalog.component';

@Component({
  selector: 'app-personal-dashboard',
  templateUrl: './personal-dashboard.component.html',
  styleUrls: ['./personal-dashboard.component.scss'],
  standalone: true,
  imports: [
    MatToolbarModule,
    MatIconModule,
    TranslateModule,
    MatMenuModule,
    MatSlideToggleModule,
    CommonModule,
    FormsModule,
    DashboardPanelComponent,
    ButtonComponent,
    RangePickerComponent,
    DatePickerComponent,
    MatSidenavModule,
    NoDataComponent,
    SpinnerComponent
  ]
})

export class ProjectDashboardComponent implements OnInit, OnDestroy {
  defaultCharts: WidgetV2[] = defaultProjectWidgetsConfig;
  @ViewChild('filterDrawer') filterDrawer: any;
  charts: WidgetV2[] = [];
  user: User = {} as User;
  projects: Project[] = [];
  startDate: string = formatISO(startOfDay(new Date()));
  endDate: string = formatISO(endOfDay(new Date()));
  showEndDate: boolean = false;
  loadingFields: boolean = false;
  showFieldList: boolean = false;
  metricFields: any[] = [];
  sliceCount: number = 20;
  allValueSelected: boolean = true;
  types: Record<string, string> = {
    "ObjectID": "title",
    "String": "title",
    "Number": "format_list_numbered",
    "Boolean": "tonality",
    "Mixed": "arrow_right",
    "Date": "date_range",
    "Array": "arrow_right"
  }
  showFilters: boolean = false;
  createdDateFilter = {
    name: "createdDate",
    type: "Date",
    enable: false,
    options: {
      dateType: "Range",
      periodAmount: 1,
      periodValue: "year",
      useMax: false,
      max: this.endDate,
      eqMax: false,
      useMin: true,
      min: this.startDate,
      eqMin: false
    }
  }
  filters: any[] = [];
  backgroundColor: string = "#1b1b1b";
  borderColor: string = "#555";
  selectedProjects: Project[] = [];
  personalDashboard: Dashboard = {} as Dashboard;
  destroy$: Subject<void> = new Subject<void>();
  refreshSignal: WritableSignal<number> = signal(0);
  exportSignal: WritableSignal<string> = signal('');
  loadingDashboard: boolean = false;
  projectsLoaded: boolean = false;

  private _canNavigateNext: boolean = true;

  get canNavigateNext(): boolean {
    return this._canNavigateNext;
  }

  constructor(
    private auth: AuthService,
    private dashboardService: DashboardService,
    private chartService: ChartService,
    private metricService: MetricService,
    private projectService: ProjectService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private translate: TranslateService,
    private pop: Pop,
    private location: Location,
  ) {
    const user = this.auth.currentUser();
    this.user = user;

    const databases: string | null = this.route.snapshot.queryParamMap.get('databases');
    const startDateParam: string | null = this.route.snapshot.queryParamMap.get('startDate');
    const endDateParam: string | null = this.route.snapshot.queryParamMap.get('endDate');

    this.showEndDate = !!endDateParam;

    if (isAfter(parseISO(this.startDate), parseISO(this.endDate))) {
      this.startDate = formatISO(sub(parseISO(this.endDate), { hours: 7 }));
    }

    if (startDateParam) {
      this.startDate = startDateParam;
    }

    if (endDateParam) {
      this.endDate = endDateParam;
    }

    if (!endDateParam && startDateParam) {
      this.endDate = formatISO(add(startOfDay(parseISO(startDateParam)), { hours: 20 }));
    }

    this.filters = [this.createdDateFilter];

    if (databases) {
      const projectIds = databases.split(',');
      this.selectedProjects = this.projects.filter(p => projectIds.includes(p.id?.toString()));
    }

    if (startDateParam) {
      this.updateDateFilter();
    }
  }

  ngOnInit(): void {
    this.personalDashboard = {} as Dashboard;
    this.startDate = formatISO(startOfDay(new Date()));
    this.endDate = formatISO(endOfDay(new Date()));
    this.getDashboard();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDashboard() {
    this.loadingDashboard = true;

    let query: any = {
      limit: 1000,
      archived: true
    }

    this.projectService.getProjects(query).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.projects = res;
      this.projectsLoaded = true;
      this.selectedProjects = [...this.projects];

      this.dashboardService.getPersonalDashboard({ owner: this.user.id }).pipe(takeUntil(this.destroy$)).subscribe(dashboards => {
        if (!dashboards.length) {
          this.addDashboard();
        } else {
          this.prepareDashboard(dashboards[0])
        }
      })
    })
  }

  refreshAllWidgets() {
    this.refreshSignal.set(this.refreshSignal() + 1);
  }

  shareExport() {
    this.exportSignal.set(`canvas_${(new Date()).getTime()}`);
  }

  prepareDashboard(dashboard: any) {
    dashboard.type = "dashboard_tab";
    dashboard.label = dashboard.title;
    this.personalDashboard = dashboard;
    this.loadingDashboard = false;
  }

  onProjectChanged() {
    this.updateParams();
    this.charts.forEach(c => c.data = [])
    this.onPickerChanged({ startDate: this.startDate, endDate: this.endDate });
  }

  onDateChanged(event: { value: { toISOString: () => any; }; }, type: string, updatePicker: any): void {
    const dateFilter = this.filters.find(f => f.name == "createdDate");

    if (!dateFilter) return;

    if (type == 'startDate') {
      dateFilter.options.min = event.value.toISOString();
    } else if (type == 'endDate') {
      dateFilter.options.max = event.value.toISOString();
    }

    const options = dateFilter.options;
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (options.dateType == "Period") {
      const now = new Date();
      let startMoment = sub(now, {
        [options.periodValue]: options.periodAmount,
        hours: 0
      });

      if (options.periodRolling) {
        startMoment = startOfDay(startMoment);
      }

      startDate = formatISO(startMoment);
      endDate = formatISO(now);
      this.showEndDate = true;
    } else {
      if (options.useMin) {
        startDate = formatISO(parseISO(options.min));
      }

      if (options.useMax) {
        endDate = formatISO(parseISO(options.max));
      }

      this.showEndDate = options.useMax;
    }

    if (updatePicker)
      this.onPickerChanged({ startDate, endDate })
  }

  updateDateFilter() {
    let dateFilter = this.filters.find(f => f.name == "createdDate");

    if (!dateFilter) {
      console.warn('Date filter not found');
      return;
    }

    dateFilter.enable = true;

    if (dateFilter && dateFilter.options.dateType == "Range") {
      dateFilter.options.min = this.startDate;
    }

    if (dateFilter && dateFilter.options.dateType == "Range") {
      dateFilter.options.useMax = this.showEndDate;
      dateFilter.options.max = this.endDate;
    }
  }

  updateParams() {
    let params: ReplayParams = { databases: this.selectedProjects.join(','), startDate: this.startDate, endDate: this.showEndDate ? this.endDate : undefined };
    if (!this.showEndDate) {
      params = { databases: this.selectedProjects.join(','), play: true, startDate: this.startDate, endDate: this.showEndDate ? this.endDate : undefined };
      this.router.navigate(
        [],
        {
          queryParams: params,
          queryParamsHandling: 'merge', // remove to replace all query params by provided
        });
    } else {
      this.location.replaceState(`/project-dashboard?databases=${this.selectedProjects.join(',')}&startDate=${params.startDate}${params.endDate ? "&endDate=" + params.endDate : ""}`)
    }
  }

  setFieldValueSelection(event: { checked: any; }, filter: { options: { selectAll: boolean; value: any[]; valueList: any; }; }) {
    if (event.checked) {
      filter.options.selectAll = true;
      filter.options.value = [null, ...filter.options.valueList];
    } else {
      filter.options.selectAll = false;
      filter.options.value = [];
    }
  }

  removeFilter(index: number) {
    this.filters.splice(index, 1)
  }

  addFilter() {
    this.showFieldList = true;

    if (!this.metricFields.length) {
      this.loadingFields = true;

      let query = {
        collection: "metric",
        limit: 50000,
      };

      this.chartService.getFields(query).pipe(takeUntil(this.destroy$)).subscribe((fields: any) => {
        let fieldArray: any[] = []

        Object.keys(fields).forEach(f => {
          if (Array.isArray(fields[f]) && fields[f].length) {
            let array: any[] = [];

            fields[f].forEach(v => {
              let lower = v.toLowerCase();
              let nbIncludeType = ["weight", "volume", "distance", "duration", "count", "length"]
              let nbEqualType = ["lat", "lon", "x", "y", "z"]

              if (lower.includes("date")) {
                array.push({
                  name: v,
                  type: "Date"
                })
              } else if (nbIncludeType.find(t => lower.includes(t)) || nbEqualType.includes(lower)) {
                array.push({
                  name: v,
                  type: "Number"
                })
              } else {
                array.push({
                  name: v,
                  type: "String"
                })
              }
            })

            fieldArray.push({
              name: f,
              type: "Array",
              options: array
            })
          } else {
            fieldArray.push({
              name: f,
              type: fields[f]
            })
          }
        })

        this.loadingFields = false;
        this.metricFields = fieldArray;
      })
    }
  }

  selectField(field: { name: string; type: any; }, option: { name: string; type: any; }) {
    this.showFieldList = false;
    let currentField: { name: string; type: any; options?: any };

    if (option) {
      currentField = {
        name: field.name + "." + option.name,
        type: option.type
      }
    } else {
      currentField = {
        name: field.name,
        type: field.type
      }
    }

    currentField.options = {};

    if (currentField.type == "String") {
      currentField.options["selectAll"] = false;
      currentField.options["valueList"] = [];
      currentField.options["value"] = [];
      this.metricService.getValueList(currentField.name).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        currentField.options["valueList"] = res.map((r: { _id: any; }) => r._id)
      })
    } else if (currentField.type == "Number") {
      currentField.options = {
        useMax: false,
        max: 0,
        eqMax: false,
        useMin: false,
        min: 0,
        eqMin: false
      }
    } else if (currentField.type == "Date") {
      currentField.options = {
        dateType: "Range",
        periodRolling: false,
        periodAmount: 1,
        periodValue: "year",
        max: "",
        eqMax: false,
        min: "",
        eqMin: false
      }
    }

    this.filters.push({ ...currentField })
  }

  saveFilter() {
    this.personalDashboard.refresh.emit("data")
  }

  editChart(widget: WidgetV2) {
    const dialogRef = this.dialog.open(WidgetEditorComponent, {
      width: '95%',
      height: '95%',
      data: {
        widget,
        selectedProjects: this.selectedProjects,
        dashboard: this.personalDashboard,
        isPersonal: true
      }
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data.dashboard) {
        this.personalDashboard.charts = data.dashboard.charts;

        if (this.personalDashboard.refresh)
          this.personalDashboard.refresh.emit("dashboard")
      }
    });
  }

  confirmDeleteChart(widget: WidgetV2) {
    this.pop.confirm(this.translate.instant('editor.confirm_delete_chart'), (confirm: { ok: any; }) => {
      if (confirm.ok) {
        this.chartService.removeChart(widget.id!).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            const cIndex = this.personalDashboard.charts?.findIndex(c => typeof c !== 'string' && c.id === widget.id);

            if (this.personalDashboard.charts && cIndex !== undefined && cIndex >= 0) {
              this.personalDashboard.charts.splice(cIndex, 1);
            }

            this.pop.success(this.translate.instant('editor.successfully_removed_chart'));
          },
          error: (error) => {
            if (error == 'offline') {
              this.pop.warning(this.translate.instant('units.impossible_you_are_offline'));
            } else {
              this.pop.error(this.translate.instant('units.error_removing_unit'))
            }
          }
        });
      }
    });
  }

  navigateDate(offset: number): void {
    const currentDate = new Date();
    const newStartDate = formatISO(add(parseISO(this.startDate), { days: offset }));
    const newEndDate = formatISO(add(parseISO(this.endDate), { days: offset }));

    if (isBefore(parseISO(newStartDate), currentDate) || isBefore(parseISO(newEndDate), currentDate)) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;

      this.updateDateFilter();

      let params: Params = {
        mode: 'replay',
        startDate: this.startDate,
        endDate: this.showEndDate ? this.endDate : undefined
      };

      if (this.showEndDate) {
        params['endDate'] = this.endDate;
      }

      this.router.navigate([], {
        queryParams: params,
        queryParamsHandling: 'merge',
      });

      this.refreshAllWidgets();
      this.verifCanNavigateNext();
    }

    this.refreshAllWidgets()
  }

  onShowFilters() {
    this.showFilters = true;
    this.filterDrawer.toggle();
  }

  onPickerChanged(event: any) {
    if (!event) return;

    const date = event.startDate || event;
    this.startDate = formatISO(startOfDay(parseISO(date)));
    this.endDate = formatISO(endOfDay(parseISO(date)));

    // this.updateDateFilter();
    // this.updateParams();

    this.navigateDate(0);
  }

  onRangePickerChanged(event: any) {
    if (!event) return;

    this.startDate = formatISO(startOfDay(parseISO(event.startDate)));
    this.endDate = formatISO(endOfDay(parseISO(event.endDate)));
    this.navigateDate(0);
  }

  onShowEndChange(): void {
    this.showEndDate = !this.showEndDate;

    if (!this.startDate || !this.endDate) return;

    if (!this.showEndDate) {
      this.endDate = formatISO(endOfDay(parseISO(this.startDate)));
    }

    const params: Params = {
      startDate: this.startDate,
      endDate: this.showEndDate ? this.endDate : null,
    };

    this.updateDateFilter();

    this.router.navigate([], {
      queryParams: params,
      queryParamsHandling: 'merge',
    });

    this.refreshAllWidgets();
    this.verifCanNavigateNext();
  }

  verifCanNavigateNext(): void {
    const today = startOfDay(new Date());
    const currentDisplayedDate = this.showEndDate ? parseISO(this.endDate) : parseISO(this.startDate);
    const nextDate = startOfDay(addDays(currentDisplayedDate, 1));
    this._canNavigateNext = isBefore(nextDate, today) || nextDate.getTime() === today.getTime();
  }

  // shareReport() {
  //   const dialogRef = this.dialog.open(ShareReportDialogComponent, {
  //     width: '420px',
  //     data: {
  //       title: this.translate.instant('workflows.reportUrl'),
  //       url: `${window.location.origin}/report?project=${this.user.currentProject.id}&key=${this.user.currentProject.flowAccessKey}&startDate=${moment(this.startDate).toISOString()}&endDate=${moment(this.endDate).toISOString()}`
  //     }
  //   })

  //   dialogRef.afterClosed().subscribe(res => {
  //   })
  // }

  // shareAccess() {
  //   const dialogRef = this.dialog.open(ShareReportDialogComponent, {
  //     width: '420px',
  //     data: {
  //       title: this.translate.instant('dashboard.share.link_title'),
  //       url: `${window.location.origin}/public?project=${this.user.currentProject.id}&key=${this.user.currentProject.flowAccessKey}&startDate=${moment(this.startDate).toISOString()}&endDate=${moment(this.endDate).toISOString()}&filters=${JSON.stringify(this.filters)}&backgroundColor=${this.backgroundColor.replace("#", "%23")}&borderColor=${this.borderColor.replace("#", "%23")}`
  //     }
  //   })

  //   dialogRef.afterClosed().subscribe(res => {
  //   })
  // }

  addDashboard() {
    const jsonDashboard = {
      title: `User Dashboard ${this.user.email}`,
      owner: this.user.id?.toString(),
      index: 0,
      charts: [],
      accessKey: '',
      mailingList: [],
      projects: [],
      projectSettings: [],
      // autoRefresh: {
      //   value: 1,
      //   unit: 'hour'
      // },
      filters: {}
    };

    this.dashboardService.addDashboard(jsonDashboard).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.prepareDashboard(res)
    });
  }

  addWidgetEditor(item: any = null): void {
    openWidgetCatalog(this.dialog, {
      title: this.translate.instant('editor.header'),
      chart: null,
      dashboard: this.personalDashboard,
      isPersonal: true
    }).subscribe(data => {
      if (data?.dashboard) {
        this.personalDashboard.charts = data.dashboard.charts;

        if (this.personalDashboard.refresh)
          this.personalDashboard.refresh.emit("dashboard")
      }
    });
  }
}